import { format } from 'date-fns';
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ColumnDef } from '@tanstack/react-table';
import { FileWithPath, useDropzone } from 'react-dropzone';
import { PhotoIcon } from '@heroicons/react/24/solid';
import AddUserModal from '../../components/AddUserModal';
import { BasicTable } from '../../components/BasicTable';
import { ClientSummary } from '../../models/Client';
import { Group } from '../../models/Group';
import { useApi } from '../../contexts/ApiContext';
import Loader from '../../icons/Loader.svg?react';
import { ToastType, useToast } from '../../contexts/ToastContext';
import { Role } from '../../models/Role';
import { useAuth } from '../../contexts/AuthContext';
import FileInput from '../../components/FileInput';

const GroupView: React.FC<{}> = () => {
  const api = useApi();
  const { id = '' } = useParams<{ id: string }>();
  const { roles } = useAuth();

  // const [group, setGroup] = useState<Group>({} as Group);
  const { addToast } = useToast();
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isUpdated, setIsUpdated] = useState<boolean>(false);
  const [setAdmin, setIsAdmin] = useState<boolean>(false);
  const [file, setFile] = useState<FileWithPath>({} as FileWithPath);
  const [isValid, setIsValid] = useState<boolean | null>(null);

  const {
    refetch,
    isFetching,
    data: group = {} as Group,
  } = useQuery<Group, Error>(['Group', id], async () => api.get(`/api/Group/Get?id=${id}`).then(({ data }) => data));

  const removeAdministrator = (userId: number): void => {
    api
      .post(`/api/Group/RemoveAdministrator`, { userId, groupId: id })
      .then(({ data }) => {
        refetch();
        addToast(`Successfully removed administrator`);
      })
      .catch((error) => {
        addToast(`Unable to remove administrator`, ToastType.Error);
      });
  };

  const onDrop = useCallback((acceptedFiles) => {
    setIsValid(true);
    setFile(acceptedFiles[0]);
  }, []);

  const { getRootProps, getInputProps } = useDropzone({
    accept: {
      'image/jpeg': ['.jpeg', '.png', '.svg'],
    },
    onDrop,
  });

  const columns = useMemo<ColumnDef<ClientSummary>[]>(
    () => [
      {
        header: 'Name',
        accessorKey: 'name',
        meta: {
          className: 'px-4 py-3 text-sm leading-5 font-medium sm:truncate',
        },
        cell: ({ row }): JSX.Element => (
          <Link className="btn-link" to={`/clients/${row.original.id}`}>
            {row.original.name}
          </Link>
        ),
      },
      {
        header: 'Contact',
        accessorKey: 'contactName',
        meta: {
          className: 'px-4 py-3 whitespace-nowrap text-sm leading-5',
        },
      },
      {
        header: 'Email',
        accessorKey: 'contactEmail',
        meta: {
          className: 'px-4 py-3 whitespace-nowrap text-sm leading-5',
        },
      },
    ],
    []
  );

  if (isFetching) {
    return (
      <div className="flex flex-col justify-center items-center h-screen">
        <Loader className="animate-spin w-6 h-6 mb-3" />
        <p className="text-sm font-medium text-gray-500">Loading</p>
      </div>
    );
  }

  const { id: groupId, name, subscriptionName, subscriptionId, contact, clients, administrator, logo } = group;

  return (
    <>
      <div className="px-4 pt-4 sm:px-6 lg:px-8">
        <div className="bg-gray-50 shadow rounded-md overflow-hidden">
          <div className="px-4 py-5 sm:px-6">
            <div className="flex-1 md:flex md:items-center md:justify-between min-w-0">
              <h3 className="text-lg leading-6 font-medium text-gray-900">Group Information</h3>
              <div className="mt-6 flex space-x-3 md:mt-0 md:ml-4">
                <span className="shadow-sm">
                  <Link to={`/groups/${id}/edit`} className="btn btn-primary">
                    Edit
                  </Link>
                </span>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-200">
            <dl>
              <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">Name</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{name}</dd>
              </div>
              <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">Administrator</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                  {administrator ? (
                    <>
                      <Link to={`/users/${administrator.id}`} className="btn btn-link mr-3">
                        {administrator.firstName} {administrator.lastName}
                      </Link>

                      {roles.includes('Administrator') && (
                        <button
                          className="inline-flex items-center justify-center px-4 py-2 border border-transparent font-medium rounded-md text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:text-sm"
                          onClick={(): void => removeAdministrator(administrator.id)}
                        >
                          Remove
                        </button>
                      )}
                    </>
                  ) : (
                    <button
                      onClick={(): void => {
                        setIsAdmin(true);
                        setIsModalOpen(true);
                      }}
                      className="btn btn-secondary"
                    >
                      Add User
                    </button>
                  )}
                </dd>
              </div>
              <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">Subscription</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                  <Link to={`/subscriptions/${subscriptionId}`} className="btn-link">
                    {subscriptionName}
                  </Link>
                </dd>
              </div>
              <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">Logo</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2 w-1/2 flex space-x-4">
                  {Object.entries(file).length !== 0 ? (
                    <FileInput
                      isValid={isValid}
                      file={file}
                      closeAction={(): void => setFile({} as FileWithPath)}
                      logType=""
                      url="/api/group/uploadlogo"
                      invalidQueries={['Group']}
                      parameters={{
                        groupId: id,
                      }}
                    />
                  ) : (
                    <div className="flex space-x-4 items-center">
                      {logo ? (
                        <img className="h-16 w-48 text-gray-400" src={`data:image/svg+xml;base64,${logo}`} alt="logo" />
                      ) : (
                        <PhotoIcon className="h-12 w-12 text-gray-400" />
                      )}

                      <div {...getRootProps()} className="btn btn-secondary h-12">
                        {' '}
                        Change
                        <input {...getInputProps()} name="file-upload" className="" accept=".jpg,.jpeg,.png" />
                      </div>
                    </div>
                  )}
                </dd>
              </div>
            </dl>
          </div>
        </div>
      </div>

      <div className="px-4 pt-4 sm:px-6 lg:px-8">
        <div className="bg-gray-50 shadow rounded-md overflow-hidden">
          <div className="px-4 py-5 sm:px-6">
            <div className="flex-1 md:flex md:items-center md:justify-between min-w-0">
              <h3 className="text-lg leading-6 font-medium text-gray-900">Clients</h3>
              <div className="mt-6 flex space-x-3 md:mt-0 md:ml-4">
                <span className="shadow-sm">
                  <Link to={`/groups/${id}/clients/0/edit`} className="btn btn-link">
                    Add Client
                  </Link>
                </span>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-200 bg-white">
            <div className="pb-6 align-middle inline-block min-w-full">
              <BasicTable columns={columns} data={clients} tablePageSize={5} />
            </div>
          </div>
        </div>
      </div>

      <div className="px-4 pt-4 sm:px-6 lg:px-8">
        <div className="bg-gray-50 shadow rounded-md overflow-hidden">
          <div className="px-4 py-5 sm:px-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900">Contact Information</h3>
          </div>
          <div className="border-t border-gray-200">
            <dl>
              <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">Name</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{contact?.name}</dd>
              </div>
              <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">Postion</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{contact?.position}</dd>
              </div>

              <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">Email</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{contact?.email}</dd>
              </div>
              <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">Phone Number</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{contact?.phone}</dd>
              </div>

              <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">Address Line 1</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{contact?.address?.addressLine1}</dd>
              </div>
              {contact?.address?.addressLine2 && (
                <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">Address Line 2</dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{contact?.address?.addressLine2}</dd>
                </div>
              )}
              <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">Suburb</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{contact?.address?.suburb}</dd>
              </div>

              <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">City</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{contact?.address?.city}</dd>
              </div>

              <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">State / Province</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{contact?.address?.state}</dd>
              </div>

              <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">Postcode / Zip</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{contact?.address?.postCode}</dd>
              </div>
            </dl>
          </div>
        </div>
      </div>

      <AddUserModal
        isOpen={isModalOpen}
        closeModal={(): void => setIsModalOpen(false)}
        hasUpdated={(): void => setIsUpdated(true)}
        groupAdmin
        groupId={groupId}
        setAdmin={setAdmin}
      />
    </>
  );
};

export default GroupView;
